import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Columns3, Repeat, Sparkles } from "lucide-react";
import { BrandLockup } from "@/components/Wordmark";
import { useKiosk } from "./KioskContext";
import { LANGS } from "./i18n";
import { KioskSettings } from "./KioskSettings";

export const CATS = [
  { key: "moto", label: "motorcycles" },
  { key: "scooter", label: "scooters" },
  { key: "ev", label: "electric" },
];

const LangSwitch = () => {
  const { lang, setLang } = useKiosk();
  return (
    <div className="flex shrink-0 rounded-full bg-[#F5F5F7] p-1" data-testid="lang-switch">
      {LANGS.map((l) => (
        <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)}
          className={`min-h-[44px] min-w-[56px] rounded-full px-3 text-[15px] font-medium transition-colors ${lang === l.code ? "bg-white text-[#1D1D1F] shadow-sm" : "text-[#6E6E73]"}`}>
          {l.label}
        </button>
      ))}
    </div>
  );
};

const usePress = (ms, fn) => {
  const h = useRef();
  return {
    onPointerDown: () => { h.current = setTimeout(fn, ms); },
    onPointerUp: () => clearTimeout(h.current),
    onPointerLeave: () => clearTimeout(h.current),
  };
};

export const KioskNav = () => {
  const { t, pulse } = useKiosk();
  const [params] = useSearchParams();
  const loc = useLocation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(false);
  const press = usePress(1500, () => setSettings(true));
  const cat = params.get("cat") || "moto";
  const onHome = loc.pathname === "/kiosk" || loc.pathname === "/kiosk/";
  useEffect(() => { document.title = "Panelo Kiosk"; }, []);

  return (
    <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-[76px] max-w-[1280px] items-center justify-between gap-4 px-6 lg:px-10">
        <button data-testid="nav-wordmark" className="min-h-[44px] shrink-0" onClick={() => navigate("/kiosk")} {...press} aria-label="Home">
          <BrandLockup size={26} pulseKey={pulse} className="hidden sm:inline-flex" />
          <BrandLockup size={20} pulseKey={pulse} className="sm:hidden" />
        </button>
        <nav className="hidden xl:flex pk:!hidden min-w-0 items-center gap-0.5 overflow-x-auto no-scrollbar whitespace-nowrap" data-testid="category-tabs">
          {CATS.map((c) => {
            const active = onHome && cat === c.key;
            return (
              <Link key={c.key} to={`/kiosk?cat=${c.key}`} data-testid={`tab-${c.key}`}
                className={`min-h-[44px] flex items-center rounded-full px-3 text-[16px] font-medium transition-colors ${active ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F] hover:bg-[#F5F5F7]"}`}>
                {t(c.label)}
              </Link>
            );
          })}
          <Link to="/kiosk/compare" data-testid="nav-compare" className={`ml-1 min-h-[44px] flex items-center gap-2 rounded-full px-3 text-[16px] font-medium ${loc.pathname.includes("compare") ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F] hover:bg-[#F5F5F7]"}`}>
            <Columns3 size={18} /> {t("compare")}
          </Link>
          <Link to="/kiosk/quiz" data-testid="nav-quiz" className={`min-h-[44px] flex items-center gap-2 rounded-full px-3 text-[16px] font-medium ${loc.pathname.includes("quiz") ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F] hover:bg-[#F5F5F7]"}`}>
            <Sparkles size={18} /> {t("findRide")}
          </Link>
          <Link to="/kiosk/exchange" data-testid="nav-exchange" className={`min-h-[44px] flex items-center gap-2 rounded-full px-3 text-[16px] font-medium ${loc.pathname.includes("exchange") ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F] hover:bg-[#F5F5F7]"}`}>
            <Repeat size={18} /> {t("exchange")}
          </Link>
        </nav>
        <LangSwitch />
      </div>
      <nav className="flex gap-1 overflow-x-auto px-4 pb-2 no-scrollbar xl:hidden pk:!flex" data-testid="category-tabs-mobile">
        {[...CATS.map((c) => ({ to: `/kiosk?cat=${c.key}`, label: t(c.label), active: onHome && cat === c.key, id: c.key })), { to: "/kiosk/compare", label: t("compare"), active: loc.pathname.includes("compare"), id: "compare" }, { to: "/kiosk/quiz", label: t("findRide"), active: loc.pathname.includes("quiz"), id: "quiz" }, { to: "/kiosk/exchange", label: t("exchange"), active: loc.pathname.includes("exchange"), id: "exchange" }].map((x) => (
          <Link key={x.id} to={x.to} data-testid={`mtab-${x.id}`} className={`min-h-[44px] flex shrink-0 items-center rounded-full px-4 text-[15px] font-medium ${x.active ? "bg-[#1D1D1F] text-white" : "bg-[#F5F5F7]"}`}>{x.label}</Link>
        ))}
      </nav>
      <KioskSettings open={settings} onOpenChange={setSettings} />
    </header>
  );
};
